import * as React from "react";
import MuiAvatar from "@mui/material/Avatar";
import MuiListItemAvatar from "@mui/material/ListItemAvatar";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Select, { selectClasses } from "@mui/material/Select";
import { styled } from "@mui/material/styles";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";

const selectPalettes = {
  dark: {
    bg: "#0b2539",
    border: "rgba(233, 245, 254, 0.035)",
    borderHover: "rgba(233, 245, 254, 0.07)",
    fieldBorder: "0.5px solid rgba(233, 245, 254, 0.11)",
    fieldBorderWidth: 0.5,
    fieldBoxShadow: "none",
    focusBorder: "rgba(233, 245, 254, 0.09)",
    hover: "rgba(233, 245, 254, 0.08)",
    fieldHover: "#102a41",
    menuBorder: "0.5px solid rgba(233, 245, 254, 0.12)",
    menuBg: "#082033",
    selectedBg: "#24394b",
    primaryText: "#ffffff",
    secondaryText: "#9fb2c2",
    mutedText: "#dcecf8",
    avatarBg: "#24394b",
    avatarBorder: "rgba(233, 245, 254, 0.14)",
    shadow: "0px 8px 30px rgba(0, 0, 0, 0.28)",
  },
  light: {
    bg: "hsl(228deg 33.33% 97.06%)",
    border: "rgba(67, 88, 108, 0.24)",
    borderHover: "rgba(67, 88, 108, 0.42)",
    fieldBorder: "1px solid rgba(67, 88, 108, 0.24)",
    fieldBorderWidth: 1,
    fieldBoxShadow: "none",
    focusBorder: "#8142fc",
    hover: "#edf2fa",
    fieldHover: "#f8fafc",
    menuBorder: "1px solid rgba(67, 88, 108, 0.24)",
    menuBg: "#ffffff",
    selectedBg: "#e7edf7",
    primaryText: "#162536",
    secondaryText: "#43586c",
    mutedText: "#43586c",
    avatarBg: "#edf2fa",
    avatarBorder: "rgba(67, 88, 108, 0.22)",
    shadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
  },
};

const Avatar = styled(MuiAvatar)({
  width: 28,
  height: 28,
});

const ListItemAvatar = styled(MuiListItemAvatar)({
  minWidth: 0,
  marginRight: 12,
});

function getListItemTextSx(colors) {
  return {
    minWidth: 0,
    "& .MuiListItemText-primary": {
      color: colors.primaryText,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiListItemText-secondary": {
      color: colors.secondaryText,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
}

export default function SelectContent({ interactive = true, sidebarDark = true }) {
  const [selectedTool, setSelectedTool] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const colors = sidebarDark ? selectPalettes.dark : selectPalettes.light;
  const listItemTextSx = getListItemTextSx(colors);
  const menuItemSx = {
    borderRadius: "6px",
    minWidth: 0,
    "&:hover": { backgroundColor: colors.hover },
    "&.Mui-selected": {
      backgroundColor: colors.selectedBg,
      "&:hover": { backgroundColor: colors.selectedBg },
    },
  };
  const avatarSx = {
    backgroundColor: colors.avatarBg,
    border: `1px solid ${colors.avatarBorder}`,
    color: colors.mutedText,
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setSelectedTool(value);

    if (value === "123crm") {
      window.open("https://painel.123crm.com.br", "_blank", "noopener,noreferrer");
      return;
    }

    if (value === "gitbook") {
      window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
      return;
    }

    if (value === "docs") {
      window.open("https://docs.123zap.com.br/", "_blank", "noopener,noreferrer");
    }
  };

  React.useEffect(() => {
    if (!interactive) {
      setOpen(false);
    }
  }, [interactive]);

  return (
    <Select
      labelId="company-select"
      id="company-simple-select"
      value={selectedTool}
      onChange={handleChange}
      open={open && interactive}
      onClose={() => setOpen(false)}
      onOpen={() => {
        if (interactive) {
          setOpen(true);
        }
      }}
      displayEmpty
      inputProps={{ "aria-label": "Select company" }}
      fullWidth
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: colors.menuBg,
            border: colors.menuBorder,
            borderRadius: "8px",
            boxShadow: colors.shadow,
            color: colors.primaryText,
            mt: 1,
            [`& .MuiList-root`]: {
              p: "8px",
            },
          },
        },
      }}
      sx={{
        backgroundColor: colors.bg,
        border: `${colors.fieldBorder} !important`,
        borderRadius: "6px",
        boxShadow: `${colors.fieldBoxShadow} !important`,
        color: colors.primaryText,
        maxHeight: 56,
        width: "100%",
        "&:hover": {
          backgroundColor: colors.fieldHover,
          color: colors.primaryText,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: `${colors.fieldBorder} !important`,
          borderColor: colors.border,
          borderWidth: `${colors.fieldBorderWidth}px !important`,
        },
        "&& .MuiOutlinedInput-notchedOutline": {
          border: `${colors.fieldBorder} !important`,
          borderColor: colors.border,
          borderWidth: `${colors.fieldBorderWidth}px !important`,
        },
        "&&& fieldset": {
          border: `${colors.fieldBorder} !important`,
          borderWidth: `${colors.fieldBorderWidth}px !important`,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: `${colors.fieldBorder} !important`,
          borderColor: colors.borderHover,
          borderWidth: `${colors.fieldBorderWidth}px !important`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: `${colors.fieldBorder} !important`,
          borderColor: colors.focusBorder,
          borderWidth: `${colors.fieldBorderWidth}px !important`,
        },
        "& .MuiSvgIcon-root": {
          color: colors.secondaryText,
        },
        "& .MuiSelect-select, & .MuiInputBase-input, & .MuiOutlinedInput-input": {
          border: 0,
          boxShadow: "none",
          outline: 0,
        },
        "&.MuiList-root": {
          p: "8px",
        },
        [`& .${selectClasses.select}`]: {
          display: "flex",
          alignItems: "center",
          gap: "2px",
          minWidth: 0,
          pl: 1,
        },
      }}
    >
      <ListSubheader
        sx={{
          backgroundColor: colors.menuBg,
          color: colors.secondaryText,
          pt: 0,
        }}
      >
        Ferramentas
      </ListSubheader>
      <MenuItem value="" sx={menuItemSx}>
        <ListItemAvatar>
          <Avatar alt="123zap" sx={avatarSx}>
            <DevicesRoundedIcon sx={{ fontSize: "1rem" }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="123zap"
          secondary="Lembretes automáticos"
          sx={listItemTextSx}
        />
      </MenuItem>
      <MenuItem value="123crm" sx={menuItemSx}>
        <ListItemAvatar>
          <Avatar alt="123crm" sx={avatarSx}>
            <DevicesRoundedIcon sx={{ fontSize: "1rem" }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="123crm"
          secondary="Atendimento omnichannel"
          sx={listItemTextSx}
        />
      </MenuItem>
      <ListSubheader
        sx={{
          backgroundColor: colors.menuBg,
          color: colors.secondaryText,
        }}
      >
        Documentações
      </ListSubheader>
      <MenuItem value="gitbook" sx={menuItemSx}>
        <ListItemAvatar>
          <Avatar alt="GitBook" sx={avatarSx}>
            <DescriptionRoundedIcon sx={{ fontSize: "1rem" }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="Documentação"
          secondary="Saiba como usar"
          sx={listItemTextSx}
        />
      </MenuItem>
      <MenuItem value="docs" sx={menuItemSx}>
        <ListItemAvatar>
          <Avatar alt="Docs 123zap" sx={avatarSx}>
            <DescriptionRoundedIcon sx={{ fontSize: "1rem" }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="API Pública"
          secondary="Endpoints disponíveis"
          sx={listItemTextSx}
        />
      </MenuItem>
    </Select>
  );
}
